import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { decodePolyline } from "modules/shared/PickupDropoff/decodePolyline";
import moment from "moment";
import {
  doGetRoute,
  getRouteList,
  assignDriver,
  reassignDriver,
  cancelRoute,
  updatePrice,
  getFacturaStatusOptions
} from "./RouteThunks";

const RouteAdapter = createEntityAdapter({
  selectId: (route) => route.id
});

const initialState = RouteAdapter.getInitialState({
  metadata: {
    currentPage: 1,
    itemsPerPage: 10
  },
  route: {},
  filter: {
    status: ["pending", "accepted", "in-progress"],
    pickupDateRange: [
      moment().subtract(1, "month").startOf("month"),
      moment().endOf("month")
    ]
  },
  sort: {
    field: "startDate",
    order: "DESC"
  },
  facturaStatusOptions: []
});

export const routeSlice = createSlice({
  name: "adminRoutes",
  initialState,
  reducers: {
    updatePaginationRoutes(state, action) {
      const oldMeta = state.metadata;
      state.metadata = {
        ...oldMeta,
        ...action.payload
      };
    },
    updateFilterRoutes(state, action) {
      state.filter = action.payload;
      state.metadata.currentPage = 1;
    },
    updateSortRoutes(state, action) {
      state.sort = action.payload;
    },
    clearRouteDetail(state) {
      // Clear route detail data to prevent memory leaks when navigating away
      state.route = {};
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getRouteList.fulfilled, (state, { payload }) => {
      console.log("RouteSlice - getRouteList.fulfilled:", {
        dataLength: payload.data?.length,
        metadata: payload.metadata,
        previousMetadata: state.metadata
      });

      // Backend now handles sorting, so we don't need to sort here
      RouteAdapter.setAll(state, payload.data);
      state.route = {};
      state.metadata = payload.metadata;
    });
    builder.addCase(doGetRoute.fulfilled, (state, { payload }) => {
      const locations = payload.routeLocations.map((routeLocation) => {
        return routeLocation.location;
      });

      state.route = {
        ...payload,
        locations,
        display: true,
        color: locations && locations[0]?.color ? locations[0]?.color : payload?.color,
        // Lazy load coordinates only when map is actually used to reduce memory usage
        polylines: payload?.polylines,
        coords: [] // Will be decoded on-demand by map component
      };
    });
    builder.addCase(assignDriver.fulfilled, (state, { payload }) => {
      RouteAdapter.updateOne(state, { id: payload.id, changes: { ...payload } });
    });
    builder.addCase(reassignDriver.fulfilled, (state, { payload }) => {
      RouteAdapter.updateOne(state, { id: payload.id, changes: { ...payload } });
    });
    builder.addCase(cancelRoute.fulfilled, (state, { payload }) => {
      RouteAdapter.updateOne(state, { id: payload.id, changes: { ...payload } });
    });
    builder.addCase(updatePrice.fulfilled, (state, { payload }) => {
      console.log("state.updatePrice", state.adminRoutes);
      console.log("payload", payload);

      // Get the current route to update both the main field and routeEstimates/finalPrices arrays
      const currentRoute = state.entities[payload.id];
      if (currentRoute) {
        const updates = { [payload.type]: payload.price };

        // Update routeEstimates array if it's an estimate price
        if (
          payload.type === "driverEstimatePrice" ||
          payload.type === "clientEstimatePrice"
        ) {
          const estimateType =
            payload.type === "driverEstimatePrice"
              ? "driver_estimate"
              : "client_estimate";

          // Add a new entry to the array to indicate history was created
          const updatedRouteEstimates = [
            ...(currentRoute.routeEstimates || []),
            {
              type: estimateType,
              estimatePrice: payload.price,
              manualUpdate: true,
              createdAt: new Date().toISOString()
            }
          ];

          updates.routeEstimates = updatedRouteEstimates;
        }

        // Update finalPrices array if it's a final price
        if (payload.type === "clientFinalPrice" || payload.type === "driverFinalPrice") {
          const finalPriceType =
            payload.type === "clientFinalPrice"
              ? "client_final_price"
              : "driver_final_price";

          // Add a new entry to the array to indicate history was created
          const updatedFinalPrices = [
            ...(currentRoute.finalPrices || []),
            {
              type: finalPriceType,
              total: payload.price,
              manualUpdate: true,
              createdAt: new Date().toISOString()
            }
          ];

          updates.finalPrices = updatedFinalPrices;
        }

        RouteAdapter.updateOne(state, {
          id: payload.id,
          changes: updates
        });
      }
    });
    builder.addCase(getFacturaStatusOptions.fulfilled, (state, { payload }) => {
      state.facturaStatusOptions = payload.data;
    });
  }
});

export const {
  selectById: selectRouteById,
  selectIds: selectRouteIds,
  selectEntities: selectRouteEntities,
  selectAll: selectAllRoutes,
  selectTotal: selectTotalRoutes
} = RouteAdapter.getSelectors((state) => state.adminRoutes);

export const selectMetadata = (state) => state.adminRoutes.metadata;
export const selectFilter = (state) => state.adminRoutes.filter;
export const selectSort = (state) => state.adminRoutes.sort;
export const selectRoute = (state) => state.adminRoutes.route;
export const selectFacturaStatusOptions = (state) =>
  state.adminRoutes.facturaStatusOptions;

export const {
  updatePaginationRoutes,
  updateFilterRoutes,
  updateSortRoutes,
  clearRouteDetail
} = routeSlice.actions;

export default routeSlice.reducer;
